import React, { useMemo, useCallback, useState, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SubcategoryRow from 'spw-programming-projects/building-details/worksheet/subcategory-row';
import {
  IProjectSpaceTypeCategory,
  IProjectSpaceTypeSubcategory,
} from 'spw-redux/modules/projects/types';
import { MACRO_PROGRAMMING_TYPE } from 'spw-constants';
import {
  generateSortingAfterDrag,
  shouldDisplayAsRatio,
} from 'spw-components/worksheet/utils';
import MacroProgrammingCategoryHeader from 'spw-components/worksheet/macro-programming-category-header';
import ProgrammingCategoryHeader from 'spw-components/worksheet/programming-category-header';
import {
  selectCurrentScenario,
  selectWorksheet,
} from 'spw-redux/modules/calculators/selectors';
import { DragDropContext, Droppable, DropResult } from 'react-beautiful-dnd';
import SpaceTypeRows from 'spw-programming-projects/building-details/worksheet/space-type-rows';
import { getListStyle } from 'spw-components/programming-scenarios/styles';
import { populateWorksheet } from 'spw-redux/modules/calculators/actions';
import { IWorksheetRow } from 'spw-redux/modules/calculators/types';
import { useChildRowsBySpaceType } from 'spw-programming-projects/building-details/worksheet/hooks';
import { IProjectSpaceType } from 'spw-redux/modules/space_types/types';
import { bulkUpdateProjectSpaceTypes } from 'spw-redux/modules/space_types/actions';
import { sortRowsByPreviousId } from 'spw-redux/modules/calculators/utils';
import {
  getParentRowsWithNestedRows,
  getRowId,
} from 'spw-programming-projects/building-details/worksheet/utils';
import Worksheet from '../../../../models/Worksheet';
import WorksheetRow from '../../../../models/WorksheetRow';
import { WorksheetContext } from './worksheet-context';

type SubcategoryRowsProps = {
  category: IProjectSpaceTypeCategory;
  subcategory: IProjectSpaceTypeSubcategory;
  isWorksheetOpenForDownload: boolean;
};

const SubcategoryRows: React.FC<SubcategoryRowsProps> = ({
  category,
  subcategory,
  isWorksheetOpenForDownload,
}) => {
  const dispatch = useDispatch();
  const worksheet = useSelector(selectWorksheet);
  const scenario = useSelector(selectCurrentScenario);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const isMacro = scenario.calculatorType === MACRO_PROGRAMMING_TYPE;

  const { collapsed } = useContext(WorksheetContext);

  const subcategoryRows: WorksheetRow[] = useMemo(() => {
    return sortRowsByPreviousId(
      worksheet.rows.filter(
        (row: WorksheetRow) =>
          row.projectSpaceTypeSubcategory?.id === subcategory.id && !row.child
      )
    ).filter((row: IWorksheetRow) => !row.removed);
  }, [worksheet.rows, subcategory.id]);

  const nonRemovedChildRowsBySpaceType = useChildRowsBySpaceType();

  const areAllParentsCollapsed = useMemo(
    () =>
      subcategoryRows.every((row) => {
        if (collapsed[getRowId(row)]) return true;

        const childRows =
          nonRemovedChildRowsBySpaceType[row.projectSpaceType.spaceType.id] ||
          [];

        // A parent row is also considered collapsed if it hasn't children
        return !childRows.length;
      }),
    [subcategoryRows, collapsed, nonRemovedChildRowsBySpaceType]
  );

  const subcategoryTotals =
    worksheet.subcategoriesTotals[
      subcategory.defaultSpaceTypeCategory.category
    ];

  const childRowsBySpaceType = useChildRowsBySpaceType(true);

  const onDragEnd = useCallback(
    (result: DropResult) => {
      if (
        !result?.destination ||
        result.source.index === result.destination.index
      ) {
        setIsDraggingOver(false);
        return;
      }

      const apiWorksheet = Worksheet.toAPIWorksheet(worksheet);
      const rows = apiWorksheet.rows.slice();

      const firstSubcategoryRowIndex = rows.findIndex(
        (row) => row.subcategory?.id === subcategory.id
      );

      const subcategoryParentRows = rows.filter(
        (row) => row.subcategory?.id === subcategory.id && !row.child
      );

      const {
        newOrderedRows,
        projectSpaceTypesToUpdate,
      } = generateSortingAfterDrag({
        rowProjectSpaceTypeId: parseInt(result.draggableId, 10),
        newIndex: result.destination.index,
        categorySpaceRows: subcategoryParentRows,
      });

      const parentRows = newOrderedRows.map((row) => new WorksheetRow(row));
      const orderedRows = getParentRowsWithNestedRows(
        parentRows,
        childRowsBySpaceType
      ).map((row: WorksheetRow) => WorksheetRow.toAPIWorksheetRow(row));

      rows.splice(firstSubcategoryRowIndex, orderedRows.length, ...orderedRows);

      dispatch(
        populateWorksheet(
          new Worksheet(
            { ...apiWorksheet, rows },
            worksheet.scenario,
            worksheet.projectSettings
          ).toPlainObject()
        )
      );

      setIsDraggingOver(false);

      dispatch(
        bulkUpdateProjectSpaceTypes(
          projectSpaceTypesToUpdate as IProjectSpaceType[]
        )
      );
    },
    [dispatch, worksheet, childRowsBySpaceType, subcategory.id]
  );

  const onBeforeCapture = () => {
    setIsDraggingOver(true);
  };

  return (
    <>
      <SubcategoryRow
        scenarioWorkSeats={scenario.workSeats}
        category={category}
        subcategory={subcategory}
      />
      {subcategoryRows.length > 0 &&
        (isMacro ? (
          <MacroProgrammingCategoryHeader
            name={subcategory.name}
            categoryOrSubcategory={subcategory}
            isWorksheetOpenForDownload={isWorksheetOpenForDownload}
            showDetailedSharedLabels={!scenario.isShared}
            isRatio={shouldDisplayAsRatio(category, subcategory)}
          />
        ) : (
          <ProgrammingCategoryHeader
            isRatio={shouldDisplayAsRatio(category, subcategory)}
            name={subcategory.name}
            categoryOrSubcategory={subcategory}
          />
        ))}
      <DragDropContext onDragEnd={onDragEnd} onBeforeCapture={onBeforeCapture}>
        <Droppable droppableId={`subcategory-${subcategory.id}`}>
          {(droppableProvided, droppableSnapshot) => (
            <div
              {...droppableProvided.droppableProps}
              ref={droppableProvided.innerRef}
              style={getListStyle(droppableSnapshot.isDraggingOver)}
            >
              {subcategoryRows.map((row: WorksheetRow, index: number) => {
                return (
                  <SpaceTypeRows
                    key={row.id}
                    row={row}
                    parentRowIndex={index}
                    totalCategorySeats={subcategoryTotals?.seats}
                    isDraggingOver={isDraggingOver}
                    areAllParentsCollapsed={areAllParentsCollapsed}
                  />
                );
              })}
              {droppableProvided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </>
  );
};

export default SubcategoryRows;
